import { Box, Card, styled, useTheme } from '@mui/material';
import { H5 } from 'components/Typography';
import Chart from 'react-apexcharts';
import FormatDate from 'utils/formatDate';

const RoomAvailabilitySummary = ({ data }) => {
    const theme = useTheme();
    const categories = data?.dates || [];
    const series = (data?.series || []).map((s) => ({
        name: s.name,
        data: s.data,
    }));

    const displayCategories = categories.map((d) =>
        FormatDate(d, 'dd MMM'),
    );

    const totals = series.map((s) =>
        (s.data || []).reduce((a, b) => a + (Number(b) || 0), 0),
    );

    const StyledChart = styled(Chart)(({ theme }) => ({
        '& .apexcharts-tooltip *': {
            fontWeight: 500,
            fontFamily: theme.typography.fontFamily,
        },
        '& .apexcharts-tooltip': {
            boxShadow: 0,
            borderRadius: 6,
            '& .apexcharts-tooltip-title': {
                display: 'block',
                width: '100%',
                backgroundColor: theme.palette.action.hover,
                padding: '6px 10px',
                marginBottom: 6,
            },
        },
        '& .apexcharts-tooltip.apexcharts-theme-light': {
            border: `1px solid ${theme.palette.divider}`,
        },
        '& .apexcharts-legend.apx-legend-position-bottom.apexcharts-align-center': {
            justifyContent: 'space-evenly',
        },
        '& .apexcharts-legend-marker': {
            borderRadius: '50% !important',
            marginRight: 5,
        },
    }));

    const options = {
        chart: {
            background: 'transparent',
            stacked: true,
            type: 'bar',
            toolbar: { show: false },
            fontFamily: theme.typography.fontFamily,
        },
        colors: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.warning.main,
            theme.palette.info.main,
            theme.palette.success.main,
        ],
        dataLabels: { enabled: false },
        grid: { show: false },
        states: {
            active: { filter: { type: 'none' } },
            hover: { filter: { type: 'none' } },
        },
        theme: { mode: theme.palette.mode },
        xaxis: {
            categories: displayCategories,
            labels: {
                show: true,
                style: {
                    colors: theme.palette.text.disabled,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 500,
                },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            show: true,
            labels: { style: { fontWeight: 500 } },
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                columnWidth: '50%',
                horizontal: false,
            },
        },
        tooltip: {
            y: { formatter: (val) => `${val}` },
        },
        legend: {
            show: true,
            position: 'bottom',
            fontSize: '13px',
            fontWeight: 500,
            onItemClick: { toggleDataSeries: false },
            onItemHover: { highlightDataSeries: false },
            offsetY: 8,
        },
    };

    return (
        <Card sx={{ padding: 3, height: '100%' }}>
            <H5 mb={3} textAlign="center">
                Hotel Room Availability
            </H5>
            {series.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: 'center',
                        mb: 2,
                    }}
                >
                    {series.map((s, idx) => (
                        <Box
                            key={s.name}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1.5,
                                bgcolor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: options.colors[idx % options.colors.length],
                                }}
                            />
                            <Box sx={{ fontSize: 13, fontWeight: 600 }}>{s.name}</Box>
                            <Box sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                                {totals[idx]}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
            <StyledChart type="bar" options={options} series={series} height={300} />
        </Card>
    );
};

export default RoomAvailabilitySummary;
